/**
 * Shared pipeline-state derivation helpers for `bundle_lifecycle` rows.
 *
 * # Why this module exists
 *
 * Before this consolidation (P0-4 from NEXT_STEPS), the same "what
 * state is this bundle in?" question had THREE slightly-different
 * implementations scattered across the SPA:
 *
 * - `pages/Bundles.tsx::deriveRoute` — returned a single-chip
 *   "Route" label (Analyzing / Queued / Analyzed / Deduped /
 *   Skipped / Failed / Intake) for the table's Route column +
 *   client-side filter
 * - `pages/Bundles.tsx::deriveStage` — returned a single-label
 *   Stage progression (Failed / Deduped / Complete / Analyzing /
 *   Dedup decided / Classified / Triaged / Retrieved / Arrived)
 *   for the table's Stage column + sort key
 * - `pages/BundleDetail.tsx::deriveStages` — returned a
 *   `StageData[]` (one entry per pipeline stage, each with
 *   `done | in_progress | pending | failed | skipped | queued`)
 *   for the PipelineStrip component
 *
 * These answered THREE different questions at three different
 * granularities — but all read the same lifecycle-row columns
 * and shared the same underlying predicates (is this bundle
 * failed? deduped? analyzing? queued? …). Any change to the
 * routing logic (e.g. the 2026-05-01 Bug 7 `queued` split)
 * required editing three files in lockstep, which is exactly
 * the kind of drift that creates the class of silent-divergence
 * bug `CONTRIBUTING.md §"Failure visibility"` exists to prevent.
 *
 * This module is the single source of truth for the shared
 * predicates + the three derived views. Every call site in the
 * SPA imports from here.
 *
 * # What lives where
 *
 * - **Predicates** (boolean helpers) at the top — `hasError`,
 *   `isSkippedAtIntake`, `isDeduped`, `isAnalyzed`, `isAnalyzing`,
 *   `isQueued`, `isIntake`. Each corresponds to ONE of the
 *   priority-ordered rules in `deriveRoute`'s decision tree;
 *   they share the `BundleLifecycleRow` input so an invariant
 *   that holds for one surface holds for all.
 * - **Types** — `RouteLabel`, `StageLabel`, `ChipColor`. The
 *   richer `StageData` / `StageStatus` types stay in
 *   `components/PipelineStrip.tsx` because they're owned by the
 *   component that renders them; this module IMPORTS them to
 *   build the `deriveStages` output.
 * - **Derivations** — `deriveRoute(row)`, `deriveStage(row)`,
 *   `deriveStages(status, fullRow)`. One function per external
 *   view.
 *
 * # Priority-ordered routing (the load-bearing contract)
 *
 * Every derivation walks the same 7-case decision tree in
 * priority order. First match wins. This order MUST stay in
 * lockstep with:
 *
 * - `src/worker/lifecycle/stores/postgres.rs::route_summary`'s
 *   CASE WHEN cascade (the Wave 12 backend aggregator)
 * - `src/worker/lifecycle/stores/in_memory.rs::route_summary`'s
 *   match cascade
 * - The trait doc comment on `BundleLifecycleStore::route_summary`
 *
 * Decision tree:
 *
 *   1. `last_error` set             → "failed"
 *   2. `classified_outcome` in
 *      {`skipped_version_gate`,
 *       `skipped_platform`,
 *       `fail`}                     → "skipped"
 *   3. `was_deduplicated = true`    → "deduped"
 *   3b. `completed_at` set AND
 *       `slack_notify_posted_at`
 *       null (TRIAG-270)           → "patchgen"
 *   4. `completed_at` set           → "analyzed"
 *   5. `analysis_started_at` set    → "analyzing"
 *   6. `dedup_decided_at` set       → "queued"
 *   7. else                         → "intake"
 *
 * INTENTIONAL DIVERGENCE (TRIAG-270 Phase 1): the backend
 * `route_summary` aggregates (Postgres CASE WHEN + in-memory
 * mirror) do NOT have a `patchgen` bucket — rows in the
 * patch-generation window count as `analyzed` there. The
 * `/bundles` page filters + chip counts are client-side over
 * `deriveRoute` (loaded-list mode), so operators see the
 * `patchgen` split everywhere that matters; only the
 * RouteSummaryStrip's pre-first-fetch window-fallback shows the
 * patchgen chip at 0 with those rows folded into Analyzed.
 * Phase 2 (`patches_started_at` column) extends the backend
 * aggregates and dissolves this divergence. Documented here so
 * a future reader doesn't "fix" it by accident — mirror of the
 * `stalled` registry-empty divergence note below.
 *
 * # Behavior contract — fixture-style examples
 *
 * The table below pins the expected output for every observed
 * lifecycle state combination. When you change any derivation
 * rule, update BOTH the code AND this table AND the
 * corresponding backend route_summary doc — divergence across
 * the three is the exact drift this module exists to prevent.
 *
 * | # | Row shape (relevant fields non-null)                                                  | deriveRoute      | deriveStage     |
 * |---|---------------------------------------------------------------------------------------|------------------|-----------------|
 * |  1| `arrived_at` (≤1h ago; older is row 18)                                              | intake           | Arrived         |
 * |  2| `arrived_at`, `retrieved_from_s3_at`                                                  | intake           | Retrieved       |
 * |  3| `arrived_at`, `retrieved_from_s3_at`, `triage_completed_at`                           | intake           | Triaged         |
 * |  4| `… + classified_at`                                                                   | intake           | Classified      |
 * |  5| `… + dedup_decided_at`, `was_deduplicated=false`                                      | queued           | Dedup decided   |
 * |  6| `… + analysis_started_at` (< 2h ago), `completed_at=null`                             | analyzing        | Analyzing       |
 * |  7| `… + analysis_started_at, analysis_finished_at, completed_at, slack_notify_posted_at`| analyzed         | Complete        |
 * |  8| `… + was_deduplicated=true, completed_at`                                             | deduped          | Deduped         |
 * |  9| `classified_outcome=skipped_version_gate`                                             | skipped          | Failed or Classified (see note) |
 * | 10| `classified_outcome=skipped_platform`                                                 | skipped          | Classified      |
 * | 11| `classified_outcome=fail`                                                             | skipped          | Classified      |
 * | 12| `last_error="some error"`                                                             | failed           | Failed          |
 * | 13| `last_error="…" + was_deduplicated=true`                                              | failed (priority)| Failed (priority)|
 * | 14| `last_error="…" + completed_at`                                                       | failed (priority)| Failed (priority)|
 * | 15| `analysis_started_at` (>2h ago), no completed_at, no last_error                       | stalled          | Analyzing       |
 * | 16| `… + completed_at`, `slack_notify_posted_at=null` (TRIAG-270 patch-gen window)        | patchgen         | Patch-gen       |
 * | 17| `… + completed_at`, `slack_notify_posted_at=null`, `last_error="…"`                   | failed (priority)| Failed (priority)|
 * | 18| `arrived_at` (>1h ago) only: no retrieval, error, dismissal, policy, dedup, outcome (#11179) | stranded         | Arrived         |
 *
 * Note on #9 vs #10/11: `deriveStage` doesn't distinguish among
 * the three skipped-outcome variants because the Stage column is
 * about pipeline position, not routing reason. All three show
 * "Classified" — the classifier ran + decided to skip; the
 * specific reason is shown in the Outcome column via
 * `classifiedOutcomeLabel`.
 *
 * # Consumers
 *
 * - `pages/Bundles.tsx` — table Route + Stage columns, Route
 *   filter, sort helpers
 * - `pages/BundleDetail.tsx` — PipelineStrip feed via
 *   `deriveStages`
 * - `components/RouteSummaryStrip.tsx` — indirectly (same enum
 *   values; the chip definitions in that component mirror the
 *   `deriveRoute` output)
 * - `components/FilterBar.tsx` — indirectly (the `route` filter
 *   field reads the same `BundleRoute` union from
 *   `filter-types.ts`; the client-side predicate reads
 *   `deriveRoute`)
 *
 * # Test coverage
 *
 * vitest is now wired into the dashboard (`npm run test`). The
 * `isTerminal` predicate is unit-tested in the co-located
 * `pipelineStages.test.ts` (added with TRIAG-242). The richer
 * derivations (`deriveRoute` / `deriveStage` / `deriveStages`)
 * still lean on:
 *
 * 1. The behavior-contract table above (14 fixture cases)
 * 2. TypeScript type checking (catches interface drift)
 * 3. Backend test coverage — the `route_summary` handler tests
 *    in `src/server/routes/api/bundles.rs::tests` assert the
 *    Postgres + in-memory stores produce identical counts on a
 *    matching row-set; the SPA's `deriveRoute` is pinned to the
 *    same rules so a test-green backend route_summary proves
 *    the SPA contract is honored too (as long as the SPA stays
 *    in lockstep — this module makes that mechanical by
 *    collapsing three sites into one).
 *
 * A future session should extend `pipelineStages.test.ts` to the
 * full 14-case `deriveRoute` fixture matrix and promote this
 * comment's table to machine-checked assertions.
 */

import type { BundleLifecycleRow, BundleStatusResponse } from "@/lib/api";
import type { BundleRoute } from "@/lib/filter-types";
import {
  PIPELINE_STAGES,
  type StageData,
  type StageName,
  type StageStatus,
} from "@/components/PipelineStrip";

// ─── Shared chip color vocabulary ───────────────────────────────────────────

/**
 * The constrained color palette every Bundles-page chip (Route +
 * Stage + derived others) picks from. Kept to a fixed union rather
 * than an open string so a future chip renderer can exhaustively
 * match on the value — any new color MUST land in this union first
 * AND in the chip-rendering switch statements in `Bundles.tsx` at
 * the same time.
 *
 * Values chosen to round-trip with Tailwind's color scale (e.g.
 * `bg-blue-50 text-blue-900 dark:bg-blue-950 dark:text-blue-100`);
 * see the chip rendering helpers in `Bundles.tsx::chipClass` for
 * the per-color class map.
 */
export type ChipColor =
  | "blue"
  | "emerald"
  | "amber"
  | "purple"
  | "slate"
  | "gray"
  | "red"
  // 2026-05-03 Slice A — `orange` introduced for the new `stalled`
  // route chip, distinct from `amber` (used by `analyzing`) so
  // operators can tell "healthy in-flight" apart from
  // "started-but-not-progressing" at a glance. Any new
  // Tailwind class-map in `Bundles.tsx::chipClass` MUST add an
  // `orange` arm alongside the existing colors.
  | "orange"
  // TRIAG-270 — `teal` introduced for the `patchgen` route chip
  // (report done, patch generation + Slack post still running).
  // Distinct from `emerald` (analyzed — the state it precedes) and
  // `amber` (analyzing — the state it follows) so the 30-60 min
  // window reads as its own phase at a glance.
  | "teal"
  // Galley #11179 — `fuchsia` for the `stranded` ("Never left
  // intake") route chip. Every warm hue is taken (amber analyzing,
  // orange stalled, red failed), and the chip must not read as any
  // of them; fuchsia is unused anywhere on the page.
  | "fuchsia";

// ─── Route derivation ───────────────────────────────────────────────────────

/**
 * The "Route" of a bundle — what happens (or happened) to it.
 * Orthogonal to `StageLabel` which tracks position within the route.
 *
 * The `route` field is the machine-facing enum value that feeds
 * the URL filter state + the route-summary chip counts; `label`
 * is the operator-facing display string; `glyph` + `color` drive
 * the chip's visual identity (rule 5 — color + label + glyph).
 */
export interface RouteLabel {
  route: BundleRoute;
  label: string;
  glyph: string;
  color: ChipColor;
}

/**
 * Derive the single-chip Route label for a bundle_lifecycle row.
 *
 * See the priority-ordered routing decision tree at the top of
 * this module for the rules. Each branch below is numbered
 * according to that decision tree.
 *
 * This function MUST stay in lockstep with the Postgres
 * `route_summary` CASE-WHEN in `stores/postgres.rs` + the
 * in-memory mirror in `stores/in_memory.rs`. A mismatch between
 * the SPA-side derivation and the backend aggregate is the exact
 * class of silent-divergence bug §"Failure visibility"
 * prohibits — the `RouteSummaryStrip` chip count would disagree
 * with the `/bundles` table row chips.
 */
export function deriveRoute(row: BundleLifecycleRow): RouteLabel {
  // (1) Failed — any pipeline error is a terminal state, overrides
  //     every downstream state. A bundle that hit an error during
  //     analysis AND had `was_deduplicated=true` written on the
  //     dedup path still shows as Failed, because the error is the
  //     operator-actionable signal.
  if (hasError(row)) {
    return { route: "failed", label: "Failed", glyph: "⚠", color: "red" };
  }

  // (2) Skipped at intake — version-gate, unsupported platform, or
  //     classifier error. Bundle never reached analysis by design.
  if (isSkippedAtIntake(row)) {
    return { route: "skipped", label: "Skipped", glyph: "⊘", color: "slate" };
  }

  // (3) Deduped — hash/vector/judge layer matched an existing
  //     canonical; no analysis ran, but the bundle IS complete
  //     (dedup-hit path writes completed_at at the same time as
  //     record_dedup per migration 006's terminal-stage CHECK).
  if (isDeduped(row)) {
    return { route: "deduped", label: "Deduped", glyph: "⚭", color: "blue" };
  }

  // (3b) Patch-generating (TRIAG-270) — report is done
  //     (`completed_at` set) but the single deferred Slack post
  //     hasn't landed because Step 8 patch generation (30-60 min
  //     under Fable xhigh) runs between them. Operators watching
  //     the dashboard previously saw "Analyzed" + no Slack message
  //     and concluded notifications were being dropped (verified
  //     2026-06-10: 67/67 posted within 24h — the gap is latency,
  //     not loss). MUST precede the `analyzed` arm: patchgen rows
  //     satisfy `completed_at IS NOT NULL` by construction.
  //     Caveats (accepted for Phase 1; exact timestamps in Phase
  //     2): patch-SKIP windows last seconds (brief chip flash); a
  //     worker killed mid-patch parks here until the SC sweep
  //     writes last_error (the failed arm then wins); a row whose
  //     Slack post failed outright parks here until the boot
  //     sweeper re-posts on the next worker start.
  if (isPatchGenerating(row)) {
    return {
      route: "patchgen",
      label: "Patch-gen",
      glyph: "📦",
      color: "teal",
    };
  }

  // (4) Analyzed — full LLM pipeline finished. Distinct from
  //     Deduped because we pay the LLM cost here.
  if (row.completed_at) {
    return {
      route: "analyzed",
      label: "Analyzed",
      glyph: "🔬",
      color: "emerald",
    };
  }

  // (5) Stalled (2026-05-03 Slice A) — analysis_started_at set
  //     ≥ 2h ago, no completed_at, no error. The 2h heuristic
  //     flavor of "prior worker crashed or got shutdown-cancelled
  //     without writing last_error." MUST precede the plain
  //     `analyzing` arm because stalled rows satisfy
  //     `analysis_started_at IS NOT NULL` by construction.
  //
  //     LOCKSTEP CONTRACT: matches the Postgres CASE WHEN branch
  //     in `src/worker/lifecycle/stores/postgres.rs::route_summary`
  //     (`NOW() - analysis_started_at > INTERVAL '2 hours'`) + the
  //     in-memory match cascade in `stores/in_memory.rs` (same
  //     2h threshold). A drift across these three is the exact
  //     silent-divergence pathology §"Failure visibility"
  //     prohibits.
  //
  //     INTENTIONAL DIVERGENCE: the SPA-side `isStalled` predicate
  //     (exported below) ALSO catches the registry-empty
  //     sub-second-precise case (a worker crashed 5 minutes ago,
  //     registry drained, last_error still NULL). That precise
  //     signal is surfaced via `StalledBundleBanner` on per-bundle
  //     pages, NOT via this chip count — the aggregate count is
  //     intentionally the persistence-only 2h heuristic because
  //     SQL can't read the in-memory registry. After the 2h mark
  //     the two converge. Documented here so a future reader
  //     doesn't "fix" the divergence.
  if (isAnalyzing(row) && isStalled2hHeuristic(row)) {
    return {
      route: "stalled",
      label: "Stalled",
      glyph: "⏸",
      color: "orange",
    };
  }

  // (6) Analyzing — strict "in flight"; analysis loop picked the
  //     bundle off its priority queue and is actively running the
  //     LLM pipeline, within the 2h healthy-duration window. This
  //     count matches the worker's `in_flight` debug log +
  //     `max_concurrent_analysis` config.
  if (isAnalyzing(row)) {
    return {
      route: "analyzing",
      label: "Analyzing",
      glyph: "🔬",
      color: "amber",
    };
  }

  // (7) Queued — `dedup_decided_at` set AND `was_deduplicated=false`
  //     means the dedup decision was "first occurrence / skipped
  //     by design" → bundle is HEADED to analysis but no worker
  //     has picked it up yet. Distinct from Analyzing (2026-04-30
  //     Bug 7 fix) so operators don't confuse backlog depth with
  //     active LLM load. A 20-row queue behind 3 in-flight
  //     analyses is TWO distinct operational signals.
  if (isQueued(row)) {
    return {
      route: "queued",
      label: "Queued",
      glyph: "⏱",
      color: "purple",
    };
  }

  // (8a) Never left intake — Galley #11179 (the owner's #11193, C).
  //     The row-1 shape (only `arrived_at`) past an hour: nothing
  //     after arrival ever happened. Before this arm it fell through
  //     to Intake below and read exactly like a report that arrived a
  //     second ago. Mirrors the backend's `stranded` CASE arm, placed
  //     just before its `ELSE 'intake'`.
  if (isNeverLeftIntake(row)) {
    return {
      route: "stranded",
      label: "Never left intake",
      glyph: "⌛",
      color: "fuchsia",
    };
  }

  // (8) Intake — pre-dedup limbo. Arrived, maybe retrieved, maybe
  //     triaged, maybe classified — but dedup hasn't decided yet.
  //     Could become analyzing / queued / deduped / skipped
  //     depending on the dedup outcome. "Intake" is the honest
  //     interim label.
  return { route: "intake", label: "Intake", glyph: "⏳", color: "gray" };
}

/** Galley #11179: how long a report may sit with only `arrived_at`
 *  before it counts as "never left intake". Past the worker's own
 *  intake recovery (1,500 s). Lockstep with the backend constant. */
export const NEVER_LEFT_INTAKE_AFTER_MS = 60 * 60 * 1000;

/**
 * Galley #11179: a report that arrived over an hour ago and never got
 * further. The same predicate as the backend `stranded` arm (#11235):
 * `retrieved_from_s3_at`, `last_error`, `dismissed_at`, `intake_policy`,
 * `was_deduplicated`, `dedup_decided_at` and `classified_outcome` all
 * null, and `arrived_at` older than {@link NEVER_LEFT_INTAKE_AFTER_MS}.
 * `last_error` is checked for null exactly as SQL does (an empty string
 * is not null), not through `hasError`. A future or unparseable
 * `arrived_at` is not stranded (conservative, like the 2h heuristic).
 */
export function isNeverLeftIntake(
  row: BundleLifecycleRow,
  nowMs: number = Date.now(),
): boolean {
  if (
    row.retrieved_from_s3_at != null ||
    row.last_error != null ||
    row.dismissed_at != null ||
    row.intake_policy != null ||
    row.was_deduplicated != null ||
    row.dedup_decided_at != null ||
    row.classified_outcome != null
  ) {
    return false;
  }
  const arrivedMs = new Date(row.arrived_at).getTime();
  if (!Number.isFinite(arrivedMs)) return false;
  return nowMs - arrivedMs > NEVER_LEFT_INTAKE_AFTER_MS;
}

// ─── Stalled 2h heuristic (2026-05-03 Slice A) ─────────────────────────────

/**
 * Persistence-only 2h heuristic for the `stalled` route — matches
 * the Postgres `NOW() - analysis_started_at > INTERVAL '2 hours'`
 * branch in `src/worker/lifecycle/stores/postgres.rs::route_summary`
 * + the in-memory `stalled_cutoff` in
 * `stores/in_memory.rs::route_summary`.
 *
 * Returns `true` only for rows where:
 *   - `analysis_started_at` is non-null AND
 *   - `analysis_started_at < now() - 2h` AND
 *   - `completed_at` is null AND
 *   - `last_error` is null
 *
 * Intentionally does NOT check the in-memory `InFlightRegistry`
 * (that's `isStalled(row, status)` below — different, more
 * precise signal surfaced via per-bundle banners). The aggregate
 * route count uses THIS heuristic; the per-bundle banner uses the
 * registry-empty variant. After 2h the two converge.
 *
 * Defensive clock-skew clamp: if `analysis_started_at` parses to a
 * future timestamp (browser clock behind the server) OR to a
 * >24h-past timestamp that might be a data glitch, the predicate
 * still returns a sensible boolean — parse errors fall back to
 * `false` (not stalled, conservative default).
 */
export function isStalled2hHeuristic(row: BundleLifecycleRow): boolean {
  if (!row.analysis_started_at) return false;
  if (row.completed_at !== null && row.completed_at !== undefined) return false;
  // Use `hasError` rather than a direct `last_error !== null` check
  // so this predicate stays consistent with the SPA's other
  // derivations. `hasError` treats a non-null but empty-string
  // `last_error` as "no error" (via the `length > 0` gate), matching
  // `isAnalyzing` + `isQueued` + `deriveStage`. There is a known
  // pre-existing divergence with the Postgres CASE WHEN (which uses
  // `last_error IS NOT NULL` with no length check — empty string
  // counts as failed in SQL but not in the SPA). This predicate
  // preserves the SPA's internal self-consistency; tightening the
  // whole-SPA `hasError` to match SQL is a separate retrofit.
  if (hasError(row)) return false;
  const startedMs = new Date(row.analysis_started_at).getTime();
  if (!Number.isFinite(startedMs)) return false;
  const nowMs = Date.now();
  const elapsedMs = nowMs - startedMs;
  const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
  const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
  // Conservative defaults: negative elapsed (browser clock behind
  // server) or elapsed > 24h (unlikely data glitch) → not stalled.
  if (elapsedMs < 0 || elapsedMs > TWENTY_FOUR_HOURS_MS) return false;
  return elapsedMs > TWO_HOURS_MS;
}

// ─── Stage derivation ───────────────────────────────────────────────────────

/**
 * The "Stage" of a bundle — compact label for pipeline position,
 * used as the sort key in the `/bundles` table's Stage column.
 *
 * Distinct from `deriveRoute` (which answers "what happens to
 * this bundle?") because Stage answers "where is it RIGHT NOW in
 * the pipeline?" — the two can coexist (e.g. a row can be Route
 * = Analyzing + Stage = Analyzing, or Route = Queued + Stage =
 * Dedup decided).
 */
export interface StageLabel {
  label: string;
  color: ChipColor;
}

/**
 * Derive the single-label Stage for a bundle_lifecycle row.
 *
 * Mirrors (roughly) the server-side
 * `BundleLifecycleRow::pipeline_stage` without requiring a
 * backend call — all the timestamps are present in the list
 * response. The backend helper returns a slightly different
 * label set (geared toward CloudWatch grep); this frontend
 * version is tuned for operator readability in the table.
 *
 * Rules (priority-ordered):
 *
 *   Failed (any last_error)      → red    "Failed"
 *   Deduped (was_dedup=true
 *     AND completed_at)          → blue   "Deduped"
 *   Complete (completed_at)      → emeral "Complete"
 *   Analyzing (analysis_started) → amber  "Analyzing"
 *   Dedup decided                → purple "Dedup decided"
 *   Classified                   → blue   "Classified"
 *   Triaged                      → blue   "Triaged"
 *   Retrieved                    → slate  "Retrieved"
 *   Arrived (default)            → slate  "Arrived"
 */
export function deriveStage(row: BundleLifecycleRow): StageLabel {
  if (hasError(row)) {
    return { label: "Failed", color: "red" };
  }
  // Deduped bundles never ran through analysis — the pipeline
  // short-circuited at the dedup layer. Labeling them "Complete"
  // (same as a real analysis-finished bundle) was confusing next
  // to `hash_miss_no_candidate` rows sitting at "Dedup decided"
  // (classified + heading to analysis). Distinct label + distinct
  // color (blue) makes the path contrast explicit:
  //   - "Deduped" (blue)   → dedup matched, no analysis ran
  //   - "Complete" (emerald) → full analysis finished
  //   - "Dedup decided" (purple) → post-dedup, pre-analysis
  // The `completed_at IS NOT NULL` check is required —
  // intake_loop sets it at the same time as `record_dedup` on
  // the hash-hit path, matching migration 006's terminal-stage
  // CHECK invariant. TRIAG-422: a duplicate analysed from the
  // family page has completed_at cleared while its own analysis
  // runs; `isDeduped` excludes it (own analysis_started_at).
  if (isDeduped(row)) {
    return { label: "Deduped", color: "blue" };
  }
  // TRIAG-270 — report done, patches + Slack post still running.
  // Same predicate as the `patchgen` route arm; see deriveRoute
  // (3b) for the full rationale + caveats.
  if (isPatchGenerating(row)) {
    return { label: "Patch-gen", color: "teal" };
  }
  if (row.completed_at) {
    return { label: "Complete", color: "emerald" };
  }
  if (row.analysis_started_at) {
    return { label: "Analyzing", color: "amber" };
  }
  if (row.dedup_decided_at) {
    return { label: "Dedup decided", color: "purple" };
  }
  if (row.classified_at) {
    return { label: "Classified", color: "blue" };
  }
  if (row.triage_completed_at) {
    return { label: "Triaged", color: "blue" };
  }
  if (row.retrieved_from_s3_at) {
    return { label: "Retrieved", color: "slate" };
  }
  return { label: "Arrived", color: "slate" };
}

// ─── Predicate helpers ──────────────────────────────────────────────────────
//
// Exported so any future consumer (e.g. a server-side filter
// chip that wants to say "show me only stuck bundles") can
// reuse the same predicate definitions instead of redefining
// them. Every predicate is pure, takes a `BundleLifecycleRow`,
// returns a boolean.

/**
 * `last_error` set to a non-empty string. Catches both the
 * classic Rust-side `record_failed` writes AND the manually-
 * inserted error rows an admin might write via psql.
 */
export function hasError(row: BundleLifecycleRow): boolean {
  return (
    row.last_error !== null &&
    row.last_error !== undefined &&
    row.last_error.length > 0
  );
}

/**
 * `classified_outcome` matches one of the three skipped-by-design
 * values. Centralized here so a new skipped variant (e.g.
 * `skipped_rate_limit` if we ever add it) lands in ONE place.
 */
export function isSkippedAtIntake(row: BundleLifecycleRow): boolean {
  return (
    row.classified_outcome === "skipped_version_gate" ||
    row.classified_outcome === "skipped_platform" ||
    row.classified_outcome === "fail"
  );
}

/**
 * `was_deduplicated = true`. The dedup layer matched this bundle
 * to an existing canonical; no analysis ran.
 */
export function isDeduped(row: BundleLifecycleRow): boolean {
  // TRIAG-422: "Deduped" means folded into its family with no analysis
  // of its own. A duplicate analysed from the family page (its own
  // `analysis_started_at`) shows Analysing / Analysed / Failed like any
  // bundle. Same rule in the SQL route CASEs (lifecycle/stores/
  // postgres.rs) and AnalysisDetail's Summary75Hero.
  return row.was_deduplicated === true && row.analysis_started_at == null;
}

/**
 * `completed_at` set AND bundle is NOT deduped AND NOT failed.
 * "A real analysis finished successfully."
 */
export function isAnalyzed(row: BundleLifecycleRow): boolean {
  return (
    row.completed_at !== null &&
    row.completed_at !== undefined &&
    !isDeduped(row) &&
    !hasError(row)
  );
}

/**
 * TRIAG-270 — the patch-generation window: the analysis report is
 * done (`completed_at` set) but the single deferred Slack post
 * hasn't landed (`slack_notify_posted_at` null), and the bundle
 * neither failed nor deduped. Between `record_completed`
 * (executor Step ~7) and `record_slack_notify_posted`, Step 8
 * patch generation runs 30-60 minutes under Fable xhigh — this
 * predicate makes that window visible instead of reading as
 * "Analyzed but the Slack message vanished."
 *
 * Derivation-only (Phase 1): both columns already ride the list +
 * detail responses; no backend change. Phase 2's
 * `patches_started_at` column would make the window exact (the
 * patch-skip case currently flashes this state for seconds).
 */
export function isPatchGenerating(row: BundleLifecycleRow): boolean {
  return (
    row.completed_at !== null &&
    row.completed_at !== undefined &&
    (row.slack_notify_posted_at === null ||
      row.slack_notify_posted_at === undefined) &&
    !isDeduped(row) &&
    !hasError(row)
  );
}

/**
 * `analysis_started_at` set AND `completed_at` NOT set — the
 * pipeline is actively running. Combined with `!hasError` to
 * distinguish from a failed analysis that happened to stamp
 * `analysis_started_at` before erroring out.
 */
export function isAnalyzing(row: BundleLifecycleRow): boolean {
  return (
    row.analysis_started_at !== null &&
    row.analysis_started_at !== undefined &&
    (row.completed_at === null || row.completed_at === undefined) &&
    !hasError(row)
  );
}

/**
 * `dedup_decided_at` set AND `was_deduplicated=false` AND no
 * `analysis_started_at` yet. Bundle is first-occurrence waiting
 * for a worker to pick it up.
 */
export function isQueued(row: BundleLifecycleRow): boolean {
  return (
    row.dedup_decided_at !== null &&
    row.dedup_decided_at !== undefined &&
    !isDeduped(row) &&
    (row.analysis_started_at === null || row.analysis_started_at === undefined)
  );
}

/**
 * Default-everything-else-is-intake predicate. Kept explicit
 * for symmetry with the other predicates and for readability
 * at call sites ("if (isIntake(row))" reads better than "else").
 */
export function isIntake(row: BundleLifecycleRow): boolean {
  return (
    !hasError(row) &&
    !isSkippedAtIntake(row) &&
    !isDeduped(row) &&
    (row.completed_at === null || row.completed_at === undefined) &&
    (row.analysis_started_at === null ||
      row.analysis_started_at === undefined) &&
    (row.dedup_decided_at === null || row.dedup_decided_at === undefined)
  );
}

/**
 * Terminal-state predicate: the bundle has reached an end state in
 * the DB — a successful completion (`completed_at` set), a failure
 * (`last_error` set), or a dedup-skip (`was_deduplicated`).
 *
 * # Why this exists (TRIAG-242, 2026-06-01)
 *
 * `completed_at` is stamped at stage ~4 of 6 in `run_job_inner`
 * (BEFORE patch generation + the Slack post — see
 * `src/worker/executor.rs`). The spawned analysis task holds its
 * `InFlightGuard` for the ENTIRE run, so from `completed_at` until
 * the task truly exits (after the patch tail + Slack post, observed
 * up to ~71 min later on bundle 34993122) the bundle reads
 * "completed" AND still has a live `in_flight` registry entry. The
 * pre-fix `BundleDetail` render gated the WorkerActiveBanner —
 * which carries the destructive Kill button — on `in_flight`
 * ALONE, so an operator saw a Kill button on a bundle the
 * dashboard already showed as completed. Clicking it only aborts
 * the patch tail of an already-persisted analysis (confusing UX,
 * not data loss). Gating the banner on `!isTerminal(row)`
 * suppresses Kill once the report lands.
 *
 * NOTE: purely presentational. The wire `in_flight` field is left
 * intact, so the server-side Restart gate (which rejects
 * actively-held rows via `is_row_restartable`) is unaffected, and
 * the server's `is_row_killable` still permits a kill while the
 * registry holds the entry — this predicate just stops the UI from
 * OFFERING the kill once the work is logically done.
 */
export function isTerminal(row: BundleLifecycleRow): boolean {
  return (
    (row.completed_at !== null && row.completed_at !== undefined) ||
    hasError(row) ||
    isDeduped(row)
  );
}

// ─── Stuck-bundle detection (Cat 2 — Reset and retry) ───────────────────────

/**
 * The "stuck bundle" predicate used by the BundleDetail page's
 * "Reset and retry" affordance + by the admin purge endpoint's
 * server-side guard.
 *
 * A bundle is stuck when it has partial lifecycle state that a
 * prior worker run wrote, and that worker didn't complete — so
 * SQS visibility-timeout redelivery would fail with
 * `LifecycleError::StageAlreadyRecorded` on the first `record_*`
 * write.
 *
 * Stuck conditions (ANY one qualifies):
 *
 * - `last_error` contains "CrashRecoveryRequired" — the new
 *   friendlier error message the Cat 2 executor wrapper emits
 *   on retry-collision. This is the PRIMARY stuck signal going
 *   forward.
 * - `last_error` contains "already recorded" — backward compat
 *   for failures written before the Cat 2 shape landed.
 * - `analysis_started_at` set AND `completed_at` not set AND
 *   `last_error` not set AND `analysis_started_at` > 2 hours
 *   ago — a real analysis that exceeded the longest observed
 *   in-flight duration. No heartbeat mechanism exists so 2
 *   hours is the empirical cutoff.
 *
 * See `src/server/routes/api/bundles.rs::post_purge`'s guard
 * for the server-side mirror of this predicate — both must
 * agree or a non-stuck row could slip through the UI button
 * gate AND fail the server guard, producing a 409.
 */
export function isStuck(row: BundleLifecycleRow): boolean {
  const err = row.last_error;
  if (err !== null && err !== undefined) {
    if (err.includes("CrashRecoveryRequired")) {
      return true;
    }
    if (err.includes("already recorded")) {
      return true;
    }
  }
  if (
    row.analysis_started_at !== null &&
    row.analysis_started_at !== undefined &&
    (row.completed_at === null || row.completed_at === undefined) &&
    !hasError(row)
  ) {
    const startedMs = new Date(row.analysis_started_at).getTime();
    const nowMs = Date.now();
    const elapsedMs = nowMs - startedMs;
    // 2 hours in ms. Clamped to [0, 24h] so a browser with a
    // miscalibrated clock doesn't mis-flag a fresh analysis as
    // stuck — if the math produces a negative or absurdly large
    // value, fall back to "not stuck" (conservative default).
    const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
    const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
    if (elapsedMs > TWO_HOURS_MS && elapsedMs < TWENTY_FOUR_HOURS_MS) {
      return true;
    }
  }
  return false;
}

/**
 * Predicate mirroring the server-side `is_row_restartable` in
 * `src/server/routes/api/bundles.rs`. Wider than `isStuck` —
 * permits the Slice 3 "Restart Analysis" button to appear on
 * any row an operator might plausibly want to re-run:
 *
 *   - Stuck rows (superset of `isStuck` — includes the Cat 2
 *     crash-recovery cases + past-2h analyzing without completion).
 *   - Completed analyses (primary Slice 3 use case: re-run
 *     against newer binary / updated prompt / refreshed source-
 *     repo tag).
 *   - Failed analyses (retry after operator investigation OR
 *     worker-binary bug fix — covers non-stuck failures like
 *     Bedrock rate-limit exhaustion, intake-classifier transient
 *     error, downstream Typst compile failure).
 *
 * REJECTS rows that are actively analyzing within the 2h
 * healthy-duration window — so an operator mis-click can't kill
 * a live LLM analysis that is already in flight.
 *
 * # Lockstep with the server
 *
 * The server-side `is_row_restartable` predicate in
 * `src/server/routes/api/bundles.rs` must implement the same
 * logic. A drift in either direction produces a bad UX:
 *
 *   - Client says restartable, server disagrees → button is
 *     visible but clicking returns 409 NOT_RESTARTABLE. The
 *     banner surfaces the server's message, but the operator
 *     wasted a click.
 *   - Client says NOT restartable, server would have allowed →
 *     the button is hidden for a row the operator could
 *     legitimately restart. They fall back to SSH + psql which
 *     is exactly the workflow Slice 3 exists to close.
 *
 * Keep both surfaces honest via the matching test coverage
 * (`is_row_restartable_covers_every_documented_branch` in
 * `bundles.rs` tests + the Jest test on the SPA side).
 */
export function isRestartable(row: BundleLifecycleRow): boolean {
  // Stuck rows are always restartable (Cat 2 crash-recovery +
  // timeout-exceeded are a strict subset of what Slice 3
  // permits — a single "Restart Analysis" button replaces the
  // need to ask "is it stuck vs failed vs completed" first).
  if (isStuck(row)) {
    return true;
  }

  // Galley #20184 (the owner's answer, #22451): a report /bundles shows as
  // "Never left intake" is restartable, the same arm as the server's
  // `shows_never_left_intake`. Restart re-sends it, which is the repair
  // intake's own recovery no longer makes.
  if (deriveRoute(row).route === "stranded") {
    return true;
  }

  // Completed analyses — primary Slice 3 use case.
  if (row.completed_at !== null && row.completed_at !== undefined) {
    return true;
  }

  // Non-stuck failures — any last_error on the row. The stuck
  // branch above already caught CrashRecoveryRequired /
  // "already recorded" shapes, so this branch handles
  // everything else (Bedrock rate-limit exhaustion, Typst
  // compile failure, etc.).
  if (row.last_error !== null && row.last_error !== undefined) {
    return true;
  }

  // Intake-only / queued / actively-analyzing-within-2h: NOT
  // restartable. These rows are doing their job or will finish
  // via the normal pipeline.
  return false;
}

/**
 * 2026-05-02 Slice B — is the analysis currently held by a worker
 * in this worker process? Reads the in-memory registry snapshot
 * that the `/api/bundles/:aid/status` endpoint surfaces on the
 * `in_flight` field.
 *
 * When `status` is undefined (not yet loaded) OR `status.in_flight`
 * is null, returns `false`. The caller should treat the "loading"
 * state as "not known to be held" to avoid flashing the Kill
 * button in + out as polls land.
 *
 * Mirrors the server-side `is_row_actively_held` predicate in
 * `src/server/routes/api/bundles.rs` — keep them in lockstep. The
 * server is authoritative; this client-side predicate is a
 * convenience for button-visibility logic.
 */
export function isActivelyHeld(
  status: { in_flight?: unknown } | undefined | null,
): boolean {
  if (!status) return false;
  return status.in_flight !== null && status.in_flight !== undefined;
}

/**
 * 2026-05-02 Slice B — is the analysis stalled? Stalled means
 * analysis_started_at is set, no completion, no error, AND no
 * active registry entry. That's the precise-detection analogue
 * of the 2h `isStuck` heuristic: we can tell in sub-second when
 * a worker crashed or got shutdown-cancelled.
 *
 * Requires BOTH the row (for the timestamp ladder) AND the status
 * (for `in_flight`). When either is missing, returns false — we
 * can't confidently call a row stalled without the registry
 * signal, and the 2h `isStuck` fallback handles the no-status
 * case safely.
 *
 * Mirrors the server-side `is_row_stalled` predicate in
 * `src/server/routes/api/bundles.rs`. Keep in lockstep.
 */
export function isStalled(
  row: BundleLifecycleRow | undefined | null,
  status: { in_flight?: unknown } | undefined | null,
): boolean {
  if (!row || !status) return false;
  if (
    row.analysis_started_at === null ||
    row.analysis_started_at === undefined
  ) {
    return false;
  }
  if (row.completed_at !== null && row.completed_at !== undefined) {
    return false;
  }
  if (row.last_error !== null && row.last_error !== undefined) {
    return false;
  }
  // No registry entry for this analysis_id — worker crashed /
  // deploy-cancelled / restart wiped the in-memory state. The
  // 2h heuristic would eventually catch this, but the registry
  // signal is sub-second precise.
  return status.in_flight === null || status.in_flight === undefined;
}

/**
 * 2026-05-02 Slice B — is the analysis killable? Only
 * actively-held rows are killable. Mirror of `isActivelyHeld`
 * with a distinct name so button-visibility logic reads
 * symmetrically with `isRestartable`:
 *
 *   if (isKillable(status)) → show Kill button
 *   else if (isRestartable(row) && !isActivelyHeld(status))
 *                             → show Restart button
 *   else                     → no action button
 *
 * Using different names for the same underlying check prevents
 * the logical-inversion mistake a reviewer would have to catch
 * otherwise. Cheap; the bundle is small.
 *
 * Mirrors the server-side `is_row_killable` predicate in
 * `src/server/routes/api/bundles.rs`.
 */
export function isKillable(
  status: { in_flight?: unknown } | undefined | null,
): boolean {
  return isActivelyHeld(status);
}

/**
 * How often `/bundles/:uuid` refetches the full row: `false` stops.
 * It polls until the row arrives, and stops once it's completed, unless
 * the duplicate's own analyse request is active (TRIAG-421) or the patch
 * step still has no outcome (Galley #12510: `patches_pending`, which the
 * server bounds by the patch sweep's window), so the patches pill reaches
 * its outcome without a reload.
 */
export function bundleRowPollInterval(
  row: BundleLifecycleRow | undefined,
  ownRequestActive: boolean,
  intervalMs: number,
): number | false {
  if (!row) return intervalMs;
  if (row.completed_at && !ownRequestActive && row.patches_pending !== true) {
    return false;
  }
  return intervalMs;
}

// ─── Full pipeline strip derivation ─────────────────────────────────────────

/**
 * Convert a `BundleStatusResponse` + optional full row into the
 * `StageData[]` shape the `PipelineStrip` component consumes on
 * the `/bundles/:uuid` detail page.
 *
 * The 8-stage ladder (arrived → retrieved → triaged → classified
 * → dedup_decided → analysis_started → analysis_finished →
 * completed) is the canonical ordered list from
 * `PipelineStrip.tsx::PIPELINE_STAGES`. Each stage's status is
 * derived as:
 *
 * - Timestamp non-null → `done`
 * - Timestamp null + bundle is deduped + stage is an analysis
 *   stage → `skipped` (the analysis pipeline never ran on this
 *   bundle)
 * - Timestamp null + some LATER stage is done (unusual but
 *   possible during backwards-incompatible writer updates) →
 *   `skipped` (the intermediate writer didn't land)
 * - Timestamp null + first-null-after-last-done position +
 *   `last_error` set → `failed`
 * - Timestamp null + first-null-after-last-done position +
 *   `analysis_started` stage AND `dedup_decided_at` is set AND
 *   NOT deduped → `queued` (Bug 7 fix: bundle is first-
 *   occurrence waiting for a worker slot)
 * - Timestamp null + first-null-after-last-done position,
 *   otherwise → `in_progress`
 * - Timestamp null + before the last-done position →
 *   `pending` (never happens today but defensive)
 *
 * The `subLabel` on `dedup_decided` carries the methodology
 * string (short form) so the pill renders "Dedup · hash_hit".
 */
export function deriveStages(
  status: BundleStatusResponse,
  fullRow: BundleLifecycleRow | undefined,
): StageData[] {
  const ts = status.timestamps;
  const timestampByStage: Record<StageName, string | null> = {
    arrived: ts.arrived_at,
    retrieved: ts.retrieved_from_s3_at,
    triaged: ts.triage_completed_at,
    classified: ts.classified_at,
    dedup_decided: ts.dedup_decided_at,
    analysis_started: ts.analysis_started_at,
    analysis_finished: ts.analysis_finished_at,
    completed: ts.completed_at,
    // TRIAG-270 — sourced from the full row (the status-endpoint
    // timestamps block predates the column). `fullRow` undefined →
    // null → the pill renders pending/in_progress from position,
    // same degradation as every other missing-row case.
    patches_notify: fullRow?.slack_notify_posted_at ?? null,
  };

  const isFailed = fullRow ? hasError(fullRow) : false;
  const wasDeduped = fullRow ? isDeduped(fullRow) : false;

  // Defensive: find the LAST done stage's index. Any stage
  // BEFORE this with a null timestamp means the intermediate
  // writer didn't land — render as `skipped` so the operator
  // doesn't see a phantom "in progress" pill counting up while
  // the real work is happening further down the strip. Any
  // stage AFTER this with a null timestamp is a legitimate
  // pending/in_progress position.
  // Galley #39931: on a failed report that never completed, a posted
  // Slack message is the failure notice, not a stage done, so it
  // doesn't turn the stage that failed into "skipped".
  const notifyIsFailureNotice = isFailed && timestampByStage.completed === null;
  let lastDoneIdx = -1;
  for (let i = PIPELINE_STAGES.length - 1; i >= 0; i--) {
    const name = PIPELINE_STAGES[i];
    if (name === "patches_notify" && notifyIsFailureNotice) continue;
    if (name !== undefined && timestampByStage[name] !== null) {
      lastDoneIdx = i;
      break;
    }
  }

  // First null AFTER lastDoneIdx = the in_progress position. If
  // every post-lastDoneIdx stage is null, the first one is
  // in_progress. If lastDoneIdx is the terminal stage, there is
  // no in_progress stage.
  let inProgressIdx = -1;
  for (let i = lastDoneIdx + 1; i < PIPELINE_STAGES.length; i++) {
    const name = PIPELINE_STAGES[i];
    if (name !== undefined && timestampByStage[name] === null) {
      inProgressIdx = i;
      break;
    }
  }

  return PIPELINE_STAGES.map((name, i) => {
    const stageTs = timestampByStage[name];
    const done = stageTs !== null;

    // Dedup-skipped downstream analysis stages: past the dedup
    // decision, the bundle was deduped, the analysis stages
    // never run. TRIAG-270: `patches_notify` is an analysis-tail
    // stage — deduped bundles never reach patch generation.
    const isAnalysisStage =
      name === "analysis_started" ||
      name === "analysis_finished" ||
      name === "completed" ||
      name === "patches_notify";
    if (wasDeduped && isAnalysisStage && !done) {
      return {
        name,
        status: "skipped" as StageStatus,
        ts: null,
        subLabel: null,
      };
    }

    // TRIAG-270 — the patches+notify pill, posted-state semantics:
    //   - posted AND patches_generated=true  → done, "patches ✓"
    //   - posted AND patches null/false      → skipped (the window
    //     ended without patches — patch-skip or patch-failure; the
    //     notify itself still happened, which the timestamp tells)
    //   - not posted → fall through to the generic position logic
    //     below (in_progress while completed is done — the 30-60
    //     min window; failed when last_error lands mid-window).
    if (name === "patches_notify" && done) {
      if (fullRow?.patches_generated === true) {
        return {
          name,
          status: "done" as StageStatus,
          ts: stageTs,
          subLabel: "patches ✓",
        };
      }
      // Galley #12510: posted, but the patch step started and has no
      // outcome yet (a deploy cut it, and the patch sweep is re-making
      // it): the patches may still come, so it isn't "skipped" yet.
      // An older server leaves the field out, which reads as before.
      if (fullRow?.patches_pending === true) {
        return {
          name,
          status: "in_progress" as StageStatus,
          ts: null,
          subLabel: null,
        };
      }
      return {
        name,
        status: "skipped" as StageStatus,
        ts: null,
        subLabel: null,
      };
    }

    // Sub-label for dedup_decided: the methodology.
    const subLabel =
      name === "dedup_decided" && done
        ? dedupMethodologyShort(status.dedup_methodology)
        : null;

    let stageStatus: StageStatus;
    if (done) {
      stageStatus = "done";
    } else if (i < lastDoneIdx) {
      // Earlier-null-but-a-later-stage-is-done: intermediate
      // writer didn't land. Render as `skipped` so the operator
      // doesn't see a phantom "in progress" pill while real
      // work is happening further down the strip.
      stageStatus = "skipped";
    } else if (i === inProgressIdx && isFailed) {
      stageStatus = "failed";
    } else if (i === inProgressIdx) {
      // Migration 026 (2026-05-12) — `dismissed` and `gated`
      // take precedence over `queued` / `in_progress` on the
      // head-of-pipeline pill. The triage decision (dismissed
      // OR gated) is the operationally-meaningful state; the
      // pre-026 rendering was `queued` for both gated and
      // genuinely-queued bundles, which misled operators about
      // why nothing was happening.
      if (fullRow?.dismissed_at) {
        stageStatus = "dismissed";
      } else if (
        name === "analysis_started" &&
        fullRow?.intake_policy === "manual" &&
        !fullRow?.analysis_started_at
      ) {
        stageStatus = "gated";
      } else {
        // 2026-05-01 Bug 7 fix: distinguish `queued` from
        // `in_progress` on the head-of-pipeline pill. A bundle is
        // queued (not in-progress) when it's a first-occurrence
        // waiting for a worker — `dedup_decided_at IS NOT NULL
        // AND NOT was_deduplicated AND analysis_started_at IS
        // NULL`. The pre-fix rendering showed "actively analyzing
        // elapsed time: 52m" on a bundle no worker had touched —
        // confusing operators into thinking the LLM had been
        // running for an hour. The `queued` branch mirrors
        // `deriveRoute`'s `queued` branch for parity.
        const isQueuedAnalysis =
          name === "analysis_started" &&
          status.timestamps.dedup_decided_at !== null &&
          !wasDeduped;
        stageStatus = isQueuedAnalysis ? "queued" : "in_progress";
      }
    } else {
      stageStatus = "pending";
    }

    return {
      name,
      status: stageStatus,
      ts: stageTs,
      subLabel,
    };
  });
}

// ─── Methodology short label (used by deriveStages sub-label) ───────────────

/**
 * Compact one-line representation of a dedup methodology for the
 * `dedup_decided` stage pill's sub-label. Kept private to this
 * module because the full methodology label (used elsewhere in
 * the dashboard) lives in `filter-types.ts::methodologyLabel`;
 * the pill sub-label needs a tighter form that fits in ~12
 * chars.
 *
 * The function is defensive against unknown values (a future
 * methodology that lands server-side before the SPA knows about
 * it renders as the raw enum string — visible + honest — rather
 * than silently dropping).
 */
function dedupMethodologyShort(methodology: string | null): string | null {
  if (methodology === null || methodology === undefined) return null;
  switch (methodology) {
    case "hash_hit":
      return "hash hit";
    case "hash_miss_vector_hit_yes":
      return "vector+LLM";
    case "hash_miss_vector_hit_no":
      return "judge said no";
    case "hash_miss_judge_transport_error":
      // Cat 3: new methodology for "every judge call transport-
      // errored." The short label matches the user-facing
      // "Judge unavailable" chip on the DedupCard.
      return "judge unavailable";
    case "hash_miss_vector_self_match_only":
      // Investigation 4 (2026-05-03): every above-threshold
      // k-NN candidate was the requester's own orphan embedding
      // (self-match) — typically produced when a prior Restart
      // Analysis click left an orphan in OpenSearch. The
      // in-loop self-match guard filtered it pre-judge and the
      // bundle was analyzed as a fresh first-occurrence. Short
      // label is lowercase to match the sibling entries; the
      // full "Vector self-match only" rendering lives in
      // `methodologyLabel` + `dedupMethodologyHuman`.
      return "vector self-match";
    case "hash_miss_no_candidate":
      return "no candidate";
    case "hash_miss_vector_transport_error":
      // TRIAG-337: the vector search itself failed (embed or
      // k-NN infrastructure error) — distinct from a clean
      // no-candidate miss. Matches the "Vector search
      // unavailable" chip in methodologyLabel.
      return "vector unavailable";
    case "first_occurrence":
      return "first occurrence";
    case "not_applicable":
      return "not applicable";
    case "not_applicable_user_filed":
      return "user-filed";
    case "not_applicable_empty_error":
      return "empty error";
    case "not_applicable_disabled":
      return "dedup disabled";
    // ── TRIAG-59 v2 sub-pattern short labels (2026-05-19) ──
    // Tight forms (≤12 chars) for the dedup_decided pipeline-
    // stage pill on /bundles. Without these the `default` arm
    // returns the raw enum string — visible but ugly
    // ("subpattern_dedup_cosine" is 24 chars and blows the cell
    // width on the table).
    case "subpattern_dedup_cosine":
      return "sub: cos match";
    case "subpattern_dedup_judge":
      return "sub: judge match";
    case "subpattern_split_cosine":
      return "sub: cos split";
    case "subpattern_split_judge":
      return "sub: judge split";
    case "subpattern_split_bootstrap":
      return "sub: bootstrap";
    case "subpattern_split_schema_drift":
      return "sub: schema drift";
    case "subpattern_skip_empty_window":
      return "sub: empty win";
    case "subpattern_skip_embed_error":
      return "sub: embed err";
    case "subpattern_skip_judge_error":
      return "sub: judge err";
    case "subpattern_skip_judge_ceiling":
      return "sub: judge cap";
    default:
      return methodology;
  }
}
